import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  TextField,
  Typography,
} from '@mui/material';

// Settings for the simple action blocks: A/B split, list actions, tag actions
// and the internal team notification.
const ActionBlockSettings = ({ block, lists = [], readOnly, onConfigChange }) => {
  const config = block.config || {};

  if (block.type === 'abSplit') {
    const percentageA = Math.min(99, Math.max(1, Number(config.percentageA) || 50));
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant='body2'>
          Variante A: <strong>{percentageA} %</strong> · Variante B:{' '}
          <strong>{100 - percentageA} %</strong>
        </Typography>
        <Slider
          value={percentageA}
          min={1}
          max={99}
          disabled={readOnly}
          valueLabelDisplay='auto'
          onChange={(event, value) => onConfigChange({ percentageA: Number(value) })}
        />
        <Typography variant='caption' color='text.secondary'>
          Variante A durchläuft den direkt folgenden Block, Variante B überspringt ihn. Die
          Zuordnung ist pro Kontakt stabil; Conversion und Umsatz je Variante erscheinen in den
          Funnel-KPIs.
        </Typography>
      </Box>
    );
  }

  if (block.type === 'addToList' || block.type === 'removeFromList') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormControl fullWidth size='small'>
          <InputLabel id={`fz-list-action-${block.blockId}`}>E-Mail-Liste</InputLabel>
          <Select
            labelId={`fz-list-action-${block.blockId}`}
            label='E-Mail-Liste'
            value={config.listId || ''}
            disabled={readOnly}
            onChange={event => onConfigChange({ listId: event.target.value || null })}
          >
            {lists.map(list => (
              <MenuItem key={list._id} value={list._id}>
                {list.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant='caption' color='text.secondary'>
          {block.type === 'addToList'
            ? 'Damit lassen sich Flows verketten: Ein anderer Flow mit dem Trigger „Angemeldet für Liste“ übernimmt den Kontakt automatisch.'
            : 'Entfernt den Kontakt aus der gewählten Liste (z. B. nach Abschluss einer Kampagne).'}
        </Typography>
      </Box>
    );
  }

  if (block.type === 'addTag' || block.type === 'removeTag') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          key={`${block.blockId}-tags`}
          size='small'
          label='Tags (kommagetrennt)'
          defaultValue={(config.tags || []).join(', ')}
          disabled={readOnly}
          onChange={event =>
            onConfigChange({
              tags: event.target.value
                .split(',')
                .map(tag => tag.trim())
                .filter(Boolean),
            })
          }
        />
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {(config.tags || []).map(tag => (
            <Chip key={tag} size='small' label={tag} />
          ))}
        </Box>
        <Typography variant='caption' color='text.secondary'>
          Tags sind das generische Segmentierungs-Werkzeug: Der Bedingungs-Block kann später auf
          „Hat Tag“ / „Hat Tag nicht“ prüfen — auch in anderen Flows.
        </Typography>
      </Box>
    );
  }

  if (block.type === 'notifyTeam') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          key={`${block.blockId}-recipients`}
          size='small'
          label='Empfänger (kommagetrennt)'
          defaultValue={(config.recipients || []).join(', ')}
          disabled={readOnly}
          onChange={event =>
            onConfigChange({
              recipients: event.target.value
                .split(',')
                .map(entry => entry.trim())
                .filter(Boolean),
            })
          }
        />
        <TextField
          key={`${block.blockId}-subject`}
          size='small'
          label='Betreff (optional)'
          defaultValue={config.subject || ''}
          disabled={readOnly}
          onChange={event => onConfigChange({ subject: event.target.value })}
        />
        <TextField
          key={`${block.blockId}-note`}
          size='small'
          label='Notiz (optional)'
          multiline
          minRows={2}
          defaultValue={config.note || ''}
          disabled={readOnly}
          onChange={event => onConfigChange({ note: event.target.value })}
        />
        <Typography variant='caption' color='text.secondary'>
          Sendet eine interne Benachrichtigung mit Kontakt- und Flow-Kontext an das Team — z. B.
          wenn ein VIP-Kunde einen bestimmten Punkt im Flow erreicht.
        </Typography>
      </Box>
    );
  }

  return null;
};

export default ActionBlockSettings;
